import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TbArrowLeft, TbCheck } from "react-icons/tb";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import moment from "moment";
import { CustomTab, CustomMRTTable, CustomLoader } from "../../../components";
import AxiosInstance from "../../../services/Api";
import styles from "./PlansPricing.module.css";

const LIST_PATH = "/dashboard/masters/plans-pricing";
const VIEW_PLAN_API = "/saas/plan/card";
const LIST_CLIENTS_API = "/saas/list/clients"; // ✅ correct endpoint

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "school", label: "School" },
];

const SCHOOL_STATUS_TABS = [
  { value: 1, label: "Active" },
  { value: 2, label: "Expired" },
  { value: 3, label: "In Discussion" },
  { value: 0, label: "Inactive" },
];

const formatPrice = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = moment(
    value,
    ["DD/MM/YYYY", "DD/MM/YYYY HH:mm", moment.ISO_8601],
    true,
  );
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "—";
};

const STATUS_STYLE = {
  1: {
    background: "#D1FAE5",
    border: "1px solid #10B981",
    color: "#059669",
    label: "Active",
  },
  0: {
    background: "#FEE2E2",
    border: "1px solid #EF4444",
    color: "#DC2626",
    label: "Inactive",
  },
};

// ─────────────────────────────────────────────────────────────────────────────

const ViewPlansPricing = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [activeTab, setActiveTab] = useState("overview");
  // ✅ status_id numeric — matches API payload
  const [schoolStatusTab, setSchoolStatusTab] = useState(1);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // ── plan detail state ─────────────────────────────────────────────────────
  const [planDetail, setPlanDetail] = useState(null);
  const [planFeatures, setPlanFeatures] = useState([]);
  const [ranges, setRanges] = useState([]);
  const [startingData, setStartingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── school/client list state ──────────────────────────────────────────────
  const [clients, setClients] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [tabCounts, setTabCounts] = useState({
    all: 0,
    active: 0,
    expired: 0,
    in_discussion: 0,
    inactive: 0,
  });
  // ✅ summary from clients API: total_schools, total_revenue, etc.
  const [summary, setSummary] = useState(null);
  const [clientsLoading, setClientsLoading] = useState(false);
  const handleSearchEnter = useCallback((value) => {
    setSearchTerm((value || "").trim());
    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // back to page 1
  }, []);

  // ── fetch plan detail ─────────────────────────────────────────────────────
  const fetchPlanDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await AxiosInstance.post(VIEW_PLAN_API, { id });

      if (response.data?.status && response.data.data) {
        const raw = response.data.data;
        const item = Array.isArray(raw) ? raw[0] : raw;

        if (!item) throw new Error("No plan data returned");

        setPlanDetail({
          id: item.id,
          name: item.plan_name,
          description: item.plan_description,
          publicBadge: item.public_badge,
          customBadgeText: item.custom_badge_text,
          highlightPlan: item.highlight_plan,
          featureType: item.feature_type,
          slabId: item.slab_id,
          status: item.status,
          createdDate: item.created_date,
          updatedDate: item.updated_date,
          featureCount: item.feature_count,
          aiFeatureCount: item.ai_count,
          totalRevenue: item.total_revenue ?? 0,
        });

        setStartingData({
          price: item.starting_price,
          maxUsers: item.starting_max_users,
          amcPrice: item.starting_amc_price,
        });

        const sortedPricing = [...(item.pricing || [])].sort(
          (a, b) => a.range_no - b.range_no,
        );
        setRanges(
          sortedPricing.map((p) => ({
            key: `range${p.range_no}`,
            upTo: p.max_users,
            planPrice: p.plan_price,
            amcPrice: p.amc_price,
          })),
        );

        setPlanFeatures(item.features || []);
      } else {
        throw new Error(
          response.data?.message || "Failed to fetch plan details",
        );
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "An error occurred",
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPlanDetail();
  }, [fetchPlanDetail]);

  // ── fetch clients (tab list only — never touches summary) ────────────────
  const fetchClients = useCallback(async () => {
    setClientsLoading(true);
    try {
      const response = await AxiosInstance.post(LIST_CLIENTS_API, {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        status: schoolStatusTab,
        plan_id: id,
        search: searchTerm,
      });
      if (response.data?.status && response.data.data) {
        setClients(response.data.data.list || []);
        setTotalClients(response.data.data.pagination?.total || 0);
        // ✅ Store tab counts to show badge numbers on tabs
        if (response.data.data.tab_counts) {
          setTabCounts(response.data.data.tab_counts);
        }
      } else {
        setClients([]);
        setTotalClients(0);
      }
    } catch (err) {
      console.error("Error fetching clients:", err);
      setClients([]);
      setTotalClients(0);
    } finally {
      setClientsLoading(false);
    }
  }, [
    id,
    schoolStatusTab,
    searchTerm,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  // ── fetch summary separately with status:"" to always get full totals ─────
  // ✅ Summary cards (Active Schools, Total Revenue) must use all-status fetch
  // so they show the plan's total regardless of which sub-tab is selected
  const fetchSummary = useCallback(async () => {
    try {
      const response = await AxiosInstance.post(LIST_CLIENTS_API, {
        page: 1,
        limit: 1, // ✅ minimal — we only need summary + tab_counts
        status: "", // ✅ all statuses → full summary
        plan_id: id,
        search: "",
      });
      if (response.data?.status && response.data.data) {
        if (response.data.data.summary) {
          setSummary(response.data.data.summary);
        }
        if (response.data.data.tab_counts) {
          setTabCounts(response.data.data.tab_counts);
        }
      }
    } catch (err) {
      console.error("Error fetching summary:", err);
    }
  }, [id]);

  // ✅ Summary fetched once on mount with status:"" — independent of tab selection
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // ✅ Client list fetched on tab/search/pagination change
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // ── school status tabs WITH counts from API ───────────────────────────────
  const schoolTabsWithCounts = useMemo(
    () => [
      {
        value: 1,
        label: `Active${tabCounts.active ? ` (${tabCounts.active})` : ""}`,
      },
      {
        value: 2,
        label: `Expired${tabCounts.expired ? ` (${tabCounts.expired})` : ""}`,
      },
      {
        value: 3,
        label: `In Discussion${tabCounts.in_discussion ? ` (${tabCounts.in_discussion})` : ""}`,
      },
      {
        value: 0,
        label: `Inactive${tabCounts.inactive ? ` (${tabCounts.inactive})` : ""}`,
      },
    ],
    [tabCounts],
  );

  // ── client table columns — mapped from API response fields ────────────────
  const clientColumns = useMemo(
    () => [
      {
        accessorKey: "client_id",
        header: "Client ID",
        size: 100,
      },
      {
        accessorKey: "school_name",
        header: "School Name",
        size: 200,
      },
      {
        accessorKey: "school_email",
        header: "School Email",
        size: 220,
      },
      {
        accessorKey: "city",
        header: "City",
        size: 160,
      },
      {
        accessorKey: "admin_name",
        header: "Admin Name",
        size: 150,
      },
      {
        accessorKey: "admin_email",
        header: "Admin Email",
        size: 220,
      },
      {
        accessorKey: "admin_mobile",
        header: "Admin Mobile",
        size: 140,
      },

      {
        accessorKey: "purchase_date",
        header: "Purchase Date",
        size: 130,
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        accessorKey: "renew_date",
        header: "Renew Date",
        size: 130,
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        // ✅ Added: status badge using status_label + status_color from API
        accessorKey: "status_label",
        header: "Status",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const label = row.original.status_label || "—";
          const color = row.original.status_color || "grey";
          const BG_MAP = {
            green: { bg: "#00A63E", text: "#fff" },
            red: { bg: "#E7000B", text: "#fff" },
            blue: { bg: "#039DD9", text: "#fff" },
            orange: { bg: "#F5821F", text: "#fff" },
            grey: { bg: "#9e9e9e", text: "#fff" },
          };
          const { bg, text } = BG_MAP[color] || BG_MAP.grey;
          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 14px",
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: 600,
                backgroundColor: bg,
                color: text,
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </span>
          );
        },
      },
      {
        // ✅ Added: action column with view icon
        id: "actions",
        header: "Action",
        size: 80,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Tooltip title="View" arrow>
            <IconButton
              size="small"
              style={{
                background: "#F5821F",
                color: "#fff",
                borderRadius: "6px",
                padding: "6px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/dashboard/masters/school/view/${row.original.purchase_order_id}`,
                );
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [navigate],
  );

  const badgeText = planDetail
    ? planDetail.publicBadge === "Others"
      ? planDetail.customBadgeText
      : planDetail.publicBadge
    : null;

  if (loading || (!planDetail && !error)) {
    return (
      <div>
        <div className={styles.loadingContainer}>
          <CustomLoader text="Loading plan details..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className={styles.card} style={{ color: "#E23B3B" }}>
          {error}
        </div>
      </div>
    );
  }

  const statusStyle = STATUS_STYLE[planDetail.status] || STATUS_STYLE[1];

  return (
    <div>
      {/* Breadcrumb */}
      {/* <div className={styles.breadcrumb}>
        <span className={styles.breadcrumbLink} onClick={() => navigate(LIST_PATH)}>
          Plans &amp; Pricing
        </span>
        <span className={styles.breadcrumbSeparator}>&gt;</span>
        <span className={styles.breadcrumbCurrent}>View Plan</span>
      </div> */}

      {/* Plan header card */}
      <div className={styles.card}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            type="button"
            onClick={() => navigate(LIST_PATH)}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              display: "flex",
              color: "#111827",
              padding: 0,
            }}
          >
            <TbArrowLeft size={30} />
          </button>

          <h1
            style={{
              margin: 0,
              fontSize: "26px",
              fontWeight: 700,
              color: "#1A1A2E",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {planDetail.name}
          </h1>

          <span
            style={{
              ...statusStyle,
              fontSize: "13px",
              fontWeight: 600,
              padding: "3px 12px",
              borderRadius: "999px",
            }}
          >
            {statusStyle.label}
          </span>
        </div>
        <p style={{ fontSize: "13px", color: "#6B7280", margin: "10px 0 0" }}>
          Updated on:{" "}
          <span style={{ color: "#111827", fontWeight: 600 }}>
            {formatDate(planDetail.updatedDate)}
          </span>
        </p>
      </div>

      {/* Stats row — data from clients API summary ── */}
      <div
        style={{
          display: "flex",
          gap: "16px",
          width: "100%",
          margin: "16px 0",
        }}
      >
        {/* Stats row — data from clients API summary */}
        <div className={styles.miniStatsRow}>
          {/* Active Schools — summary.total_schools from /saas/list/clients */}
          <div className={`${styles.miniStatCard} ${styles.miniStatCardBlue}`}>
            <span
              className={`${styles.miniStatValue} ${styles.miniStatValueBlue}`}
            >
              {summary?.total_schools ?? tabCounts.all ?? 0}
            </span>
            <span className={styles.miniStatLabel}>Active Schools</span>
          </div>

          {/* Total Revenue */}
          <div className={`${styles.miniStatCard} ${styles.miniStatCardGreen}`}>
            <span
              className={`${styles.miniStatValue} ${styles.miniStatValueGreen}`}
            >
              {formatPrice(summary?.total_revenue ?? planDetail.totalRevenue)}
            </span>
            <span className={styles.miniStatLabel}>Total Revenue</span>
          </div>
        </div>
      </div>

      {/* Main tabs */}
      <div className={styles.tabsWrapper}>
        <CustomTab tabs={TABS} value={activeTab} onChange={setActiveTab} />
      </div>

      {/* ── OVERVIEW TAB ── */}
      {activeTab === "overview" && (
        <div className={styles.tabPanel}>
          {/* Plan preview card */}
          <div className={styles.card}>
            <h2 className={styles.sectionTitle}>Preview</h2>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "24px 0",
              }}
            >
              <div
                style={{
                  width: "340px",
                  boxSizing: "border-box",
                  border: "1.5px solid #E5E7EB",
                  borderRadius: "16px",
                  padding: "28px 24px",
                  fontFamily: "inherit",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  overflowWrap: "anywhere",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "8px",
                    marginBottom: "12px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "20px",
                      fontWeight: 700,
                      color: "#1D4ED8",
                      minWidth: 0,
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {planDetail.name}
                  </span>
                  {badgeText && (
                    <span
                      style={{
                        background: "#CCFBF1",
                        color: "#0F766E",
                        fontSize: "12px",
                        fontWeight: 600,
                        padding: "4px 10px",
                        borderRadius: "20px",
                        maxWidth: "100%",
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {badgeText}
                    </span>
                  )}
                </div>

                <div
                  style={{
                    color: "#6B7280",
                    fontSize: "13px",
                    marginBottom: "4px",
                  }}
                >
                  Starting at
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: "6px",
                    lineHeight: 1,
                  }}
                >
                  <span
                    style={{
                      fontSize: "36px",
                      fontWeight: 800,
                      color: "#111827",
                    }}
                  >
                    {formatPrice(startingData?.price)}
                  </span>
                  <span style={{ fontSize: "13px", color: "#6B7280" }}>
                    / year
                  </span>
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#6B7280",
                    marginTop: "8px",
                    marginBottom: "20px",
                  }}
                >
                  for up to{" "}
                  {Number(startingData?.maxUsers || 0).toLocaleString("en-IN")}{" "}
                  users
                </div>

                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "14px",
                    color: "#111827",
                    marginBottom: "10px",
                  }}
                >
                  Slab Pricing by Users
                </div>
                <div
                  style={{
                    background: "#F9FAFB",
                    borderRadius: "10px",
                    overflow: "hidden",
                    marginBottom: "20px",
                  }}
                >
                  {ranges.map((range, i) => (
                    <div
                      key={range.key}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "10px 14px",
                        borderBottom:
                          i < ranges.length - 1 ? "1px solid #E5E7EB" : "none",
                      }}
                    >
                      <span style={{ fontSize: "14px", color: "#374151" }}>
                        Up to {Number(range.upTo).toLocaleString("en-IN")} users
                      </span>
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#111827",
                        }}
                      >
                        {formatPrice(range.planPrice)}/year
                      </span>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    color: "#6B7280",
                    marginBottom: "8px",
                  }}
                >
                  INCLUDES
                </div>
                <ul
                  style={{ margin: "0 0 20px", padding: 0, listStyle: "none" }}
                >
                  {planDetail.featureCount > 0 && (
                    <li
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "6px",
                        fontSize: "14px",
                      }}
                    >
                      <TbCheck size={16} color="#22C55E" />
                      <span>
                        {planDetail.featureCount}+ Core Features
                        {planDetail.aiFeatureCount > 0 && (
                          <> + {planDetail.aiFeatureCount} AI Features</>
                        )}
                      </span>
                    </li>
                  )}

                  {[
                    "One Time Setup",
                    "Product Training",
                    "Unlimited Cloud Storage",
                    "24/7 support for all issues",
                  ].map((item) => (
                    <li
                      key={item}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "6px",
                        fontSize: "14px",
                      }}
                    >
                      <TbCheck size={16} color="#22C55E" />
                      {item}
                    </li>
                  ))}
                </ul>

                {startingData && (
                  <div
                    style={{
                      background: "#EFF6FF",
                      borderRadius: "10px",
                      padding: "14px 16px",
                    }}
                  >
                    {/* ✅ AMC label */}
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 400,
                        color: "#1D4ED8",
                        marginBottom: "4px",
                      }}
                    >
                      AMC Price around
                    </span>
                    {/* ✅ Bold large price — matches Image 1 */}
                    <span
                      style={{
                        paddingLeft: "3px",
                        fontSize: "15px",
                        fontWeight: 800,
                        color: "#1D4ED8",
                        marginBottom: "10px",
                      }}
                    >
                      {formatPrice(startingData.amcPrice)}
                    </span>
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        color: "#6B7280",
                        marginBottom: "6px",
                      }}
                    >
                      INCLUDES
                    </div>
                    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                      {["Cloud Support", "24/7 support for all issues"].map(
                        (item) => (
                          <li
                            key={item}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginBottom: "4px",
                              fontSize: "13px",
                            }}
                          >
                            <TbCheck size={14} color="#22C55E" />
                            {item}
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Plan Features */}
          <div className={styles.card}>
            <div className={styles.cardHeaderRow}>
              <h2
                className={styles.sectionTitle}
                style={{
                  marginBottom: 0,
                  paddingBottom: 0,
                  borderBottom: "none",
                }}
              >
                Plan Features
              </h2>
              <span className={styles.sectionCount}>
                {planFeatures.length} Features
              </span>
            </div>
            <ul className={styles.checklist}>
              {planFeatures.map((feature) => (
                <li key={feature.id} className={styles.checklistItem}>
                  <TbCheck size={16} className={styles.checklistIcon} />
                  <span>
                    <span className={styles.checklistName}>{feature.name}</span>
                    {" - "}
                    <span className={styles.checklistDesc}>
                      {feature.description}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* ── SCHOOL TAB ── */}
      {activeTab === "school" && (
        <div className={styles.tabPanel}>
          <div className={styles.card}>
            <h2 className={styles.sectionTitle}>School List</h2>

            {/* ✅ Status sub-tabs — clicking changes status payload & refetches */}
            <div className={styles.tabsWrapper}>
              <CustomTab
                tabs={schoolTabsWithCounts}
                value={schoolStatusTab}
                onChange={(val) => {
                  setSchoolStatusTab(val); // ✅ numeric status_id
                  setPagination((prev) => ({ ...prev, pageIndex: 0 })); // reset to page 1
                  setSearchTerm(""); // reset search
                  setSearchInput("");
                }}
              />
            </div>

            {/* Client table — columns match API response fields */}
            <CustomMRTTable
              columns={clientColumns}
              data={clients}
              rowCount={totalClients}
              loading={clientsLoading} // ✅ wrapper maps `loading` → MRT state.isLoading
              manualPagination
              manualFiltering={true} // ✅ server-side filtering (was false → broke search)
              pagination={pagination}
              onPaginationChange={setPagination}
              enablePagination
              enableSorting
              enableSearch
              searchInput={searchInput}
              setSearchInput={setSearchInput}
              onSearchEnter={handleSearchEnter}
              placeholder="Search Client ID or School"
              enableColumnFilters={false}
              enableColumnVisibility={false}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableHiding={false}
              enableColumnActions={false}
              enableStickyHeader
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewPlansPricing;
